import React, { useRef, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { FileSearch, Upload, Loader2, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ListenButton } from './ListenButton';

export const DenialExplainer = ({ onUse }) => {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const inp = useRef();

  const run = async () => {
    if (!text.trim() && !file) return toast.error('Paste the letter text or upload a PDF/photo');
    setBusy(true);
    const fd = new FormData();
    if (file) fd.append('file', file);
    fd.append('text', text);
    try { const { data } = await api.post('/appeals/explain', fd); setOut(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not read the letter'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 sm:p-6 mt-6 bg-[#EBF3FA] border-[#B8D5E5]" data-testid="denial-explainer">
      <div className="font-semibold flex items-center gap-2 text-[#13426B]" style={{fontFamily: 'Lexend'}}><FileSearch className="w-4 h-4" /> Explain my denial letter</div>
      <p className="text-sm text-[#13426B]/80 mt-1">Paste the letter or upload a PDF/photo. Claude reads it and tells you, in plain words, why they said no and what to do next. Nothing is stored except that you used this tool.</p>
      {!out ? (
        <div className="mt-3 space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Dear applicant, we have reviewed your application for… " className="bg-card" data-testid="explain-text" />
          <div className="flex items-center gap-2 flex-wrap">
            <input ref={inp} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="explain-file" />
            <Button type="button" variant="outline" size="sm" onClick={() => inp.current?.click()} data-testid="explain-upload"><Upload className="w-3.5 h-3.5 mr-1" /> {file ? file.name.slice(0, 28) : 'Upload PDF or photo'}</Button>
            <Button size="sm" onClick={run} disabled={busy} className="bg-[#1E5888] hover:bg-[#1E5888]/90 text-white" data-testid="explain-run">{busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Reading…</> : 'Explain it'}</Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-3 text-sm" data-testid="explain-result">
          <div className="flex items-center gap-2 flex-wrap">
            {out.program && <Badge className="bg-[#1E5888] text-white border-0" data-testid="explain-program">{out.program.toUpperCase()}</Badge>}
            {out.decision_date && <Badge variant="outline" data-testid="explain-date">Letter dated {out.decision_date}</Badge>}
            {out.is_denial === false && <Badge variant="outline">This may not be a denial</Badge>}
            <ListenButton text={`${out.summary}. ${(out.reasons || []).join('. ')}. ${(out.next_steps || []).join('. ')}`} label="explanation" testId="explain-listen" />
          </div>
          <p className="text-base" data-testid="explain-summary">{out.summary}</p>
          {out.reasons?.length > 0 && <div><div className="font-medium">Why they said no</div><ul className="list-disc pl-5 mt-1 space-y-0.5">{out.reasons.map((r) => <li key={r}>{r}</li>)}</ul></div>}
          {out.missing?.length > 0 && <div><div className="font-medium">What they say was missing</div><ul className="list-disc pl-5 mt-1 space-y-0.5">{out.missing.map((r) => <li key={r}>{r}</li>)}</ul></div>}
          {out.deadline_hint && <p className="bg-[#FFF4E5] text-[#663C00] rounded-lg p-2.5" data-testid="explain-deadline">{out.deadline_hint}</p>}
          {out.next_steps?.length > 0 && <div><div className="font-medium">Do this next</div><ol className="list-decimal pl-5 mt-1 space-y-0.5">{out.next_steps.map((r) => <li key={r}>{r}</li>)}</ol></div>}
          <div className="flex gap-2 flex-wrap pt-1">
            {out.program && <Button size="sm" onClick={() => onUse(out)} className="bg-[#C26227] hover:bg-[#C26227]/90 text-white" data-testid="explain-use">Build my appeal steps from this <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>}
            <Button size="sm" variant="ghost" onClick={() => { setOut(null); setFile(null); }} data-testid="explain-reset">Try another letter</Button>
          </div>
          <p className="text-xs text-muted-foreground">AI summary (Claude Sonnet 5) — always check against the letter itself. Not legal advice.</p>
        </div>
      )}
    </Card>
  );
};
